import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import './App.css';

// Lazy-load pages that aren't needed on first paint
const Services = lazy(() => import('./pages/Services'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Verification = lazy(() => import('./pages/Verification'));
const Category = lazy(() => import('./pages/Category'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const Account = lazy(() => import('./pages/Account'));
const Cart = lazy(() => import('./pages/Cart'));
const Payment = lazy(() => import('./pages/Payment'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Gdpr = lazy(() => import('./pages/Gdpr'));

// Lazy-load entire admin panel
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminResetPassword = lazy(() => import('./pages/admin/AdminResetPassword'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminVerifications = lazy(() => import('./pages/admin/AdminVerifications'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminAddonServices = lazy(() => import('./pages/admin/AdminAddonServices'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderStatus = lazy(() => import('./pages/OrderStatus'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCertificates = lazy(() => import('./pages/admin/AdminCertificates'));
const AdminManagement = lazy(() => import('./pages/admin/AdminManagement'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', color: '#888' }}>
    Loading...
  </div>
);

function App() {
  return (
    <Router>
      <Routes>
        {/* Admin Routes - fully lazy, own Suspense */}
        <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
        <Route path="/admin/forgot-password" element={<Suspense fallback={<PageLoader />}><AdminForgotPassword /></Suspense>} />
        <Route path="/admin/reset-password/:token" element={<Suspense fallback={<PageLoader />}><AdminResetPassword /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
          <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
          <Route path="verifications" element={<Suspense fallback={<PageLoader />}><AdminVerifications /></Suspense>} />
          <Route path="certificates" element={<Suspense fallback={<PageLoader />}><AdminCertificates /></Suspense>} />
          <Route path="products" element={<Suspense fallback={<PageLoader />}><AdminProducts /></Suspense>} />
          <Route path="categories" element={<Suspense fallback={<PageLoader />}><AdminCategories /></Suspense>} />
          <Route path="addon-services" element={<Suspense fallback={<PageLoader />}><AdminAddonServices /></Suspense>} />
          <Route path="orders" element={<Suspense fallback={<PageLoader />}><AdminOrders /></Suspense>} />
          <Route path="reviews" element={<Suspense fallback={<PageLoader />}><AdminReviews /></Suspense>} />
          <Route path="contacts" element={<Suspense fallback={<PageLoader />}><AdminContacts /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<PageLoader />}><AdminSettings /></Suspense>} />
          <Route path="admins" element={<Suspense fallback={<PageLoader />}><AdminManagement /></Suspense>} />
        </Route>
        {/* Public Routes - Header/Footer always render, only page content lazy */}
        <Route path="/*" element={
          <div className="app">
            <Header />
            <main>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/verification" element={<Verification />} />
                  <Route path="/c/:slug" element={<Category />} />
                  <Route path="/pricelist/:slug" element={<ProductDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/password-reset" element={<PasswordReset />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order/:orderNumber" element={<OrderStatus />} />
                  <Route path="/payment" element={<Payment />} />
                  <Route path="/confirmation" element={<Confirmation />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/gdpr" element={<Gdpr />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
